import { format, isSameDay, parseISO } from 'date-fns'
import humanizeString from 'humanize-string'
import React, { FC, ReactElement } from 'react'
import { includes } from 'lodash'
import { useTranslation } from 'react-i18next'
import { TableDivider } from '../../../components/Common/TableDivider'
import { dividedActionsCategories } from '../../../constants'
import { Action, ActionsOrderBy } from '../../../graphql'
import { actionsPageStateValue } from '../../../graphql/local'
import { usePageFiltersSorting } from '../../../hooks'

export const DividerByActionSortValue: FC<{
  actions: Action[]
  index: number
  children: ReactElement
}> = ({ actions, index, children }) => {
  const { t } = useTranslation()

  const { pageFilters } = usePageFiltersSorting(actionsPageStateValue)
  const { sortBy } = pageFilters

  if (!includes(dividedActionsCategories, sortBy) || actions.length <= 5) {
    return <>{children}</>
  }

  const prevAction = actions[index - 1]
  const currentAction = actions[index]
  const nextAction = actions[index + 1]

  const excludeSubstringRegEx = /(Signing|B2BDebit)/gi

  const isNotLastRow = index + 1 !== actions.length

  const dividerFactory = {
    [ActionsOrderBy.Type]: () => ({
      isShowDivider: currentAction?.type !== prevAction?.type,
      isBorderBottomHidden: currentAction?.type !== nextAction?.type && isNotLastRow,
      dividerText: humanizeString(currentAction.type.replace(excludeSubstringRegEx, '')),
    }),

    [ActionsOrderBy.Status]: () => ({
      isShowDivider: currentAction?.status !== prevAction?.status,
      isBorderBottomHidden: currentAction?.status !== nextAction?.status && isNotLastRow,
      dividerText: t(currentAction.status || ''),
    }),

    [ActionsOrderBy.CreatedAt]: () => ({
      isShowDivider: !isSameDay(
        new Date(currentAction?.createdAt),
        new Date(prevAction?.createdAt),
      ),
      isBorderBottomHidden:
        !isSameDay(new Date(currentAction?.createdAt), new Date(nextAction?.createdAt)) &&
        isNotLastRow,
      dividerText: format(parseISO(currentAction?.createdAt), 'dd.MM.yyyy'),
    }),
  }

  const { isShowDivider, isBorderBottomHidden, dividerText } = dividerFactory[
    sortBy as keyof typeof dividerFactory
  ]()

  return (
    <>
      {isShowDivider && <TableDivider dividerText={dividerText} />}
      {React.cloneElement(children, { hideBorderBottom: isBorderBottomHidden })}
    </>
  )
}
